import {
  StoredReport,
  getAvailableColumns,
  getColumnLabel,
} from '@hogwarts/scheme-profiles';
import { ActionWithChildren, useDebounce } from '@hogwarts/ui-components-core';
import { ApiParams, Column, ColumnApi } from '@hogwarts/ui-components-grid';
import { has, sortBy, uniq } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GridView, PageHeader } from '../../../../components';
import {
  AnalyticsContext,
  FederationContext,
  OrganisationContext,
  UserContext,
} from '../../../../context';
import {
  useBuildReport,
  useIsFirstMount,
  useMutation,
  usePermission,
} from '../../../../hooks';
import {
  DELETE_REPORT,
  UPDATE_REPORT,
  UPDATE_REPORT_OPTIONS,
  UPDATE_REPORT_OPTIONS_Type,
} from '../../../../mutations';
import {
  addConcatField,
  addProfileTypeField,
  addRatingField,
  addSchemeField,
  addTagField,
  addTemplateField,
  gridOptions as defaultGridOptions,
  useDataSourceFactory,
} from '../../../profilesGridUtils';
import ConditionalPanel, {
  ConditionalPanelOnChangeContext,
} from './conditionalPanel';
import ReportColorsPanel, {
  ReportColorRules,
  ReportColorsPanelOnChangeContext,
} from './reportColorsPanel';

import { GridOptions } from '@ag-grid-community/core';
import { ConditionGroup } from '@hogwarts/conditionals';
import permissions from '@hogwarts/permissions';
import { evaluateGroup } from '@hogwarts/scheme-profiles';
import { veryCleanObject } from '@hogwarts/utils';
import log from '@scrtracker/logging';
import { useHistory } from 'react-router-dom';
import { ConditionalScheme } from '../../../../components/Conditionals';
import { GET_REPORTS } from '../../../../queries';
import { getExportTypeProps } from '../../utils';
import ProfilesFilterBar from './filterBar';
import styles from './styles.module.css';
import { useEditReportForm } from './useEditReportForm';

const gridOptions: GridOptions = {
  ...defaultGridOptions,
  noRowsOverlayComponent: 'profileListNoRowsOverlay',
  rowSelection: 'single',
  // sizeColumnsToFit: true,
  defaultColDef: {
    ...defaultGridOptions.defaultColDef,
    width: 150,
    filter: true,
    floatingFilter: true,
    editable: false,
    suppressMenu: false,
  },

  sideBar: {
    ...(defaultGridOptions?.sideBar as {}),
    toolPanels: [
      {
        id: 'conditional',
        labelDefault: 'Conditions',
        labelKey: 'conditional', //?
        iconKey: 'filter',
        toolPanel: 'conditionalPanel',
      },
      {
        id: 'reportColors',
        labelDefault: 'Report Colors',
        labelKey: 'reportColors',
        iconKey: 'tint',
        toolPanel: 'reportColorsPanel',
      },
    ],
  },
  suppressRowClickSelection: true,
  suppressCellSelection: true,
  processCellForClipboard: ({ column, value }) => {
    const colDef = column.getColDef();
    if (colDef.field === 'typeKey') {
      return value?.key;
    }
    return value;
  },
};

interface InsightOptionsColumn {
  key: string;
}
interface InsightOptions {
  columns: InsightOptionsColumn[];

  // profileTypes: unknown[];

  // excel: any;
  // pdf: any;

  // // filter?
  // type: string;
  // values: any[];
  // condition?: any;

  // tags: unknown[];
  // ratings: unknown[];  // profileTypes: filters.profileTypes ?? [],
  // tags: filters.tags ?? [],
  // ratings: ratingFilter,
}

const getReportOptions = (
  currentFilter: InsightOptions,
  columnApi: ColumnApi
) => {
  //https://www.ag-grid.com/javascript-grid/column-state/
  let columnState = columnApi?.getColumnState();
  if (!columnState) return;

  const sort = sortBy(
    columnState.filter((c) => c.sort),
    'sortIndex'
  ).map((c) => ({
    field: c.colId,
    desc: c.sort !== 'asc',
  }));

  const allColumns = uniq([
    ...columnState.map((c) => c.colId),
    ...currentFilter.columns.map((c) => c.key),
  ]);

  const columns = allColumns.map((columnId) => {
    const existing = currentFilter.columns.find(
      (column) => column.key === columnId
    );

    const state = columnState.find((state) => state.colId === columnId);

    if (!state) {
      return existing;
    }

    return veryCleanObject({
      ...existing,
      key: state.colId,
      width: state.width,
      visible: state.hide !== true,
      pinned: state.pinned,
    });
  });

  return {
    ...currentFilter,
    sort,
    columns,
  };
};

const useAutoSave = ({
  enabled,
  onSave,
}: {
  enabled: boolean;
  onSave: () => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const markChanged = useDebounce(async () => {
    if (!enabled) return;
    console.log('Debounce: Saving Report');
    log.info('Saving Report', {});
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  }, SAVE_DEBOUNCE_TIME);

  return {
    isSaving,
    markChanged,
  };
};

const SAVE_DEBOUNCE_TIME = 950;

const frameworkComponents = {
  conditionalPanel: ConditionalPanel,
  reportColorsPanel: ReportColorsPanel,
};

const convertFormatToGridCellStyle = (
  format: any,
  wrap: boolean
): Record<string, string | number> | null => {
  const result: Record<string, string | number> = {};
  if (format?.fill) result.backgroundColor = format.fill;
  if (wrap) result['whiteSpace'] = 'pre-wrap';
  if (!Object.keys(result).length) return null;
  return result;
};

const ProfilesGrid = ({ insight }: { insight: StoredReport }) => {
  const organisation = useContext(OrganisationContext);
  const federation = useContext(FederationContext);
  const analytics = useContext(AnalyticsContext);
  const user = useContext(UserContext);

  const history = useHistory();

  const isFirstMount = useIsFirstMount();

  const isOwner = insight.ownerType === 'USER' && insight.ownerId === user?.id;
  const hasPermission = usePermission(permissions.REPORT_UPDATE, insight.ownerId);
  const canEdit = !!(
    !insight.locked &&
    insight.ownerType !== 'SYSTEM' &&
    (isOwner || hasPermission)
  );
  const canExport = usePermission(permissions.PROFILE_PRINT, organisation.id);
  const [currentFilter, setCurrentFilter] = useState<any>(insight.options);

  const [{ api: gridApi, columnApi }, setGridApi] = useState<
    Partial<ApiParams>
  >({});

  const getRagHex = useMemo(() => {
    const normalize = (c?: string) => {
      if (!c || typeof c !== 'string') return null;
      return c.startsWith('#') ? c : `#${c}`;
    };

    const defaults = {
      red: '#FF0000',
      amber: '#f6e58d',
      green: '#2ecc71',
    };

    // Some builds still use older generated types that don't include reportColors yet.
    // Runtime data includes it when queried, so we safely access it via a narrow cast.
    const org = (organisation as any)?.reportColors || {};
    const fed = (federation as any)?.reportColors || {};

    // Priority: federation overrides organisation overrides defaults
    return {
      red: normalize(fed.red) || normalize(org.red) || defaults.red,
      amber: normalize(fed.amber) || normalize(org.amber) || defaults.amber,
      green: normalize(fed.green) || normalize(org.green) || defaults.green,
    } as const;
  }, [(federation as any)?.reportColors, (organisation as any)?.reportColors]);

  const getReportColorFillForCell = useMemo(() => {
    const rules: ReportColorRules | undefined = currentFilter?.reportColorRules;
    const fillCache = new Map<string, Record<string, string | null>>();
    const matchedKeysCache = new Map<
      string,
      { green: Set<string> | null; amber: Set<string> | null; red: Set<string> | null }
    >();

    const buildDataPack = (profile: any) => {
      const values = profile?.data || {};
      const visible: Record<string, boolean> = {};
      const valid: Record<string, boolean> = {};

      const scheme = profile?.scheme;
      const baseScheme = organisation?.scheme;
      const fields = baseScheme?.fields || [];
      for (const field of fields) {
        const key = field?.key;
        if (!key || !scheme?.getField) continue;
        try {
          const f = scheme.getField(key);
          if (f) {
            visible[key] = !!f.visible;
            valid[key] = !!f.valid;
          }
        } catch {
          // ignore fields not present for this profile type
        }
      }

      return { values, visible, valid };
    };

    const hasMeaningfulConditions = (group?: ConditionGroup | null): boolean => {
      if (!group || !Array.isArray(group.conditions) || group.conditions.length === 0) {
        return false;
      }
      for (const c of group.conditions) {
        if (!c) continue;
        if ('conditions' in (c as any)) {
          if (hasMeaningfulConditions(c as any)) return true;
          continue;
        }
        if (typeof (c as any).when !== 'undefined') {
          return true;
        }
      }
      return false;
    };

    const evaluate = (group?: ConditionGroup | null, profile?: any): boolean => {
      if (!hasMeaningfulConditions(group)) return false;
      if (!group) return false;
      const dataPack = buildDataPack(profile);
      return (
        evaluateGroup(group, {}, dataPack, { timezone: organisation?.timezone }) ===
        true
      );
    };

    const isVarKey = (v: unknown): v is string =>
      typeof v === 'string' && v.length > 0 && v.endsWith('$');

    /** Subject side of each leaf (`when`); not comparison params (decider field). */
    const collectSubjectVarKeysFromNode = (node: any, out: Set<string>) => {
      if (!node) return;
      if (node.conditions && Array.isArray(node.conditions)) {
        for (const child of node.conditions) {
          collectSubjectVarKeysFromNode(child, out);
        }
        return;
      }
      if (isVarKey(node.when)) out.add(node.when);
    };

    const fieldKeysFromVarKeys = (varKeys: Set<string>, variables: any) => {
      const keys = new Set<string>();
      const vars = variables || {};
      for (const vk of Array.from(varKeys)) {
        const v = vars[vk];
        if (
          v &&
          typeof v !== 'string' &&
          Array.isArray((v as any).source)
        ) {
          const [sourceType, sourceKey] = (v as any).source;
          if (sourceType === 'field' && typeof sourceKey === 'string') {
            keys.add(sourceKey);
          }
        }
      }
      return keys;
    };

    const getMatchedFieldKeysForGroup = (
      group?: ConditionGroup | null,
      profile?: any
    ): Set<string> | null => {
      if (!hasMeaningfulConditions(group)) return null;
      if (!group) return null;

      const operator = group.operator || 'and';
      const vars = group.variables || {};

      // Negated operators don't map cleanly to "matched branch keys"
      if (operator === 'nor' || operator === 'nand') return null;

      const dataPack = buildDataPack(profile);

      const evalChild = (child: any): boolean => {
        if (child && child.conditions && Array.isArray(child.conditions)) {
          const childGroup = child as ConditionGroup;
          const mergedVars = { ...vars, ...(childGroup.variables || {}) };
          return (
            evaluateGroup(
              { ...childGroup, variables: mergedVars } as any,
              {},
              dataPack,
              { timezone: organisation?.timezone }
            ) === true
          );
        }
        return (
          evaluateGroup(
            {
              operator: 'and',
              enabled: true,
              variables: vars,
              conditions: [child],
            } as any,
            {},
            dataPack,
            { timezone: organisation?.timezone }
          ) === true
        );
      };

      const keysForNode = (node: any, variablesForNode: any) => {
        const used = new Set<string>();
        collectSubjectVarKeysFromNode(node, used);
        return fieldKeysFromVarKeys(used, variablesForNode);
      };

      if (operator === 'or') {
        const union = new Set<string>();
        let anyPass = false;
        for (const child of group.conditions || []) {
          if (!child) continue;
          if (!evalChild(child)) continue;
          anyPass = true;
          const variablesForNode =
            child &&
            'conditions' in (child as any) &&
            Array.isArray((child as any).conditions)
              ? { ...vars, ...((child as ConditionGroup).variables || {}) }
              : vars;
          for (const k of Array.from(keysForNode(child, variablesForNode))) {
            union.add(k);
          }
        }
        return anyPass ? union : null;
      }

      // AND: union keys from all children (all must pass)
      const union = new Set<string>();
      for (const child of group.conditions || []) {
        if (!child) continue;
        if (!evalChild(child)) return null;
        const variablesForNode =
          child && 'conditions' in (child as any) && Array.isArray((child as any).conditions)
            ? { ...vars, ...((child as ConditionGroup).variables || {}) }
            : vars;
        for (const k of Array.from(keysForNode(child, variablesForNode))) union.add(k);
      }
      return union;
    };

    return (profile: any, columnKey?: string | null): string | null => {
      const id = profile?.id;
      if (!id || !columnKey) return null;
      if (!rules) return null;

      if (fillCache.has(id) && fillCache.get(id)![columnKey] !== undefined) {
        return fillCache.get(id)![columnKey]!;
      }

      if (!fillCache.has(id)) {
        fillCache.set(id, {});
      }

      let fill: string | null = null;

      let matchedKeys = matchedKeysCache.get(id);
      if (!matchedKeys) {
        matchedKeys = {
          green: getMatchedFieldKeysForGroup(rules.green as any, profile),
          amber: getMatchedFieldKeysForGroup(rules.amber as any, profile),
          red: getMatchedFieldKeysForGroup(rules.red as any, profile),
        };
        matchedKeysCache.set(id, matchedKeys);
      }

      const canApply = (color: 'green' | 'amber' | 'red') => {
        const keys = matchedKeys?.[color];
        if (!keys) return false; // rule didn't match
        return keys.size === 0 || keys.has(columnKey); // empty => apply to all
      };

      if (canApply('red')) fill = getRagHex.red;
      else if (canApply('amber')) fill = getRagHex.amber;
      else if (canApply('green')) fill = getRagHex.green;

      fillCache.get(id)![columnKey] = fill;
      return fill;
    };
  }, [currentFilter?.reportColorRules, getRagHex, organisation?.scheme, organisation?.timezone]);
  
  const isCustomReport = insight.ownerType !== 'SYSTEM';
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(isCustomReport);

  const computedGridOptions = useMemo(() => ({
    ...gridOptions,
    // Grid-level settings to prevent column manipulation
    suppressMovable: !canEdit,
    suppressRowDrag: canEdit,
    suppressMoveWhenRowDragging: canEdit,
    // Disable sidebar completely when report is locked to prevent column management
    sideBar: canEdit ? gridOptions.sideBar : false,
    // Column-level default settings
    defaultColDef: {
      ...gridOptions.defaultColDef,
      sortable: canEdit,
      resizable: canEdit,
      suppressMovable: !canEdit,
    },
  }), [canEdit]);

  const [updateReport] = useMutation(UPDATE_REPORT, {
    selector: 'updateReport',
    refetchQueries: [
      {
        query: GET_REPORTS,
        variables: {
          organisationKey: organisation.key,
        },
      },
    ],
  });
  const [deleteReport] = useMutation(DELETE_REPORT, {
    refetchQueries: [
      {
        query: GET_REPORTS,
        variables: {
          organisationKey: organisation.key,
        },
      },
    ],
  });
  const [saveReportState] = useMutation<UPDATE_REPORT_OPTIONS_Type>(
    UPDATE_REPORT_OPTIONS,
    {
      variables: {
        reportId: insight?.id,
        reportOptions: {},
      },
    }
  );

  const [groups, columns] = useMemo(
    () => {
      // Get a list of all the available columns using sources
      // and apply the report state
      const [groups] = getAvailableColumns(
        insight.options,
        organisation.scheme
      );

      // turn the columns into grid columns.
      let gridGroups = [];
      let gridColumns = [];

      for (const group of groups) {
        let gridGroup: any = {
          ...group,
          columns: [],
        };
        gridGroups.push(gridGroup);
        for (const column of group.columns!) {
          let gridColumn: Column;

          switch (column.type) {
            case 'tag': {
              // @ts-ignore
              gridColumn = addTagField();
              break;
            }
            case 'profileType': {
              // @ts-ignore
              gridColumn = addProfileTypeField(organisation.scheme);
              break;
            }
            case 'rating': {
              // @ts-ignore
              gridColumn = addRatingField(column.systemKey);
              break;
            }
            case 'template': {
              // @ts-ignore
              gridColumn = addTemplateField(column.references, column.template);
              break;
            }
            case 'concat': {
              // @ts-ignore
              gridColumn = addConcatField(
                column.fields || [],
                column.join || '\n'
              );
              break;
            }
            case 'field':
            default: {
              // @ts-ignore
              gridColumn = addSchemeField(
                column.key,
                organisation.scheme,
                column.dataType
              );
              break;
            }
          }

          gridColumn = {
            autoHeight: column.wrap ? true : undefined,
            wrapText: column.wrap ? true : undefined,
            cellStyle: column.wrap ? { whiteSpace: 'pre-wrap' } : undefined,
            ...gridColumn,
            key: column.key!,
            type: column.dataType,
            group: gridGroup.key,
            label: column.meta
              ? getColumnLabel(column.label, column.meta)
              : column.label!,
            width: typeof column.width === 'number' ? column.width : undefined,
            sort: column.sort,
            sortIndex: column.sortIndex,
            sortable: canEdit,
            resizable: canEdit,
            lockPosition: !canEdit,
            suppressMovable: !canEdit,
            meta: {
              ...column.meta,
              locked: column.locked,
            },
            editable: false,
            visible: column.visible !== false,
            pinned: column.pinned,
          };

          if (column.formatSelectors) {
            gridColumn.cellStyle = ({ data: profile, value }) => {
              if (column.getCellStyle) {
                const format = column.getCellStyle(profile, value);
                const result = convertFormatToGridCellStyle(
                  format,
                  !!column.wrap
                );
                const fill = getReportColorFillForCell(profile, column.key);
                if (fill) {
                  return {
                    ...(result || {}),
                    backgroundColor: fill,
                  };
                }
                return result;
              }
            };
          } else {
            const existing = gridColumn.cellStyle;
            gridColumn.cellStyle = (params: any) => {
              const fill = getReportColorFillForCell(params?.data, column.key);
              const base =
                typeof existing === 'function'
                  ? existing(params)
                  : existing || null;
              if (!fill) return base;
              return {
                ...(base || {}),
                backgroundColor: fill,
              };
            };
          }

          gridGroup.columns.push(gridColumn);
          gridColumns.push(gridColumn);
        }
      }

      return [gridGroups, gridColumns];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organisation.scheme, canEdit, getReportColorFillForCell]
  );

  const dataSource = useDataSourceFactory({
    variables: {
      includeShared: insight.options.includeShared,
      includeFederation: (insight.options as any)?.includeFederation,
      dynamicCondition: insight.options.dynamicCondition,
      includeExpiringFields: insight.options.includeExpiringFields,
    },
  });

  const [editReportForm] = useEditReportForm({
    report: insight,
    onDelete: async () => {
      await deleteReport({
        variables: {
          reportId: insight?.id,
        },
      });
      history.push(`/${organisation.key}/insights`);
    },
    onUpdate: async (values: any) => {
      const options: InsightOptions = {
        ...insight.options,
        ...values.options,
        excel: {
          ...insight.options?.excel,
          ...values.options?.excel,
        },
        pdf: {
          ...insight.options?.pdf,
          ...values.options?.pdf,
        },
      };
      await updateReport({
        variables: {
          ...values,
          options,
          reportId: insight.id,
        },
      });
      setCurrentFilter({
        ...currentFilter,
        ...options,
      });
    },
  });

  const { isSaving, markChanged } = useAutoSave({
    enabled: canEdit && autoSaveEnabled,
    onSave: () => {
      if (isFirstMount) return;
      return saveReport();
    },
  });

  useEffect(
    () => {
      if (autoSaveEnabled) {
        markChanged('AutoSave Enabled');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoSaveEnabled]
  );

  useEffect(
    () => {
      if (isFirstMount) return;
      markChanged('Filter/Conditional Changed');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFilter]
  );

  // Monitor for column changes
  useEffect(() => {
    const handler = (event: any) => markChanged(`Grid Event: ${event.type}`);

    const events = ['columnResized', 'sortChanged', 'displayedColumnsChanged'];

    for (const event of events) {
      gridApi && gridApi.addEventListener(event, handler);
    }

    // gridColumnsChanged
    // columnEverythingChanged
    return () => {
      for (const event of events) {
        gridApi && gridApi.removeEventListener(event, handler);
      }
    };
  }, [gridApi, markChanged]);

  const contextMenuItems = useCallback(
    (nodes) => {
      return [
        nodes.length === 1 && {
          name: nodes[0].data.name,
          disabled: true,
        },
        nodes.length > 1 && {
          name: { message: 'Selected {{count}} rows', count: nodes.length },
          disabled: true,
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        nodes.length === 1 && {
          name: 'View Profile',
          action: async () => {
            let profileId = nodes[0].data.id;
            if (profileId) {
              analytics.events.profilesGrid.viewProfileClicked();
              history.push(`/${organisation.key}/profiles/${profileId}`);
            }
          },
        },
      ];
    },
    [analytics.events.profilesGrid, history, organisation.key]
  );

  const [
    exportReport,
    {
      component: BuildingReportComponent,
      props: reportProps,
      disabled: reportDisabled,
    },
  ] = useBuildReport();

  const saveReport = useCallback(() => {
    // analytics.track(
    //   analytics.trackingKeys.profiles.ADVANCED_EDITOR_CLICKED
    // );

    return saveReportState({
      variables: {
        reportOptions: getReportOptions(currentFilter, columnApi!),
      },
    });
  }, [columnApi, currentFilter, saveReportState]);

  const actions = useMemo<ActionWithChildren[]>(() => {
    const actions: ActionWithChildren[] = [];
    if (canEdit && insight.ownerType !== 'SYSTEM') {
      actions.push({
        text: 'Edit',
        icon: 'pencil',
        allowed: canEdit,
        intent: 'none',
        onClick: () => {
          analytics.events.insights.editDetail();
          // Calculate the correct ownerType for tag-based reports
          const getCorrectOwnerType = () => {
            const reportOwnerType = insight.ownerType;
            const organisationTags = insight.options?.organisationTags;
            
            if (reportOwnerType === 'FEDERATION' && 
                organisationTags && organisationTags.length > 0) {
              const tagKey = organisationTags[0];
              return `TAG:${tagKey.toLowerCase()}`;
            }
            
            return reportOwnerType || 'USER';
          };
          
          const correctOwnerType = getCorrectOwnerType();
          
          const initialValues = {
            name: insight.name,
            description: insight.description,
            ownerType: correctOwnerType,
            options: {
              ...insight.options,
              excel: {
                includeHeaders: true,
                includeSummary: true,
                ...insight.options.excel,
              },
            },
          };
          return editReportForm({ initialValues });
        },
      });
    }
    actions.push({
      text: `Autosave ${autoSaveEnabled ? 'ON' : 'OFF'}`,
      icon: isSaving ? 'spinner' : 'save',
      spin: isSaving,
      disabled: isSaving || insight.ownerType === 'SYSTEM',
      allowed: canEdit,
      intent: autoSaveEnabled ? 'success' : 'danger',
      onClick: () => {
        const newState = !autoSaveEnabled;
        analytics.events.insights.toggleAutoSave({
          enabled: newState,
        });
        setAutoSaveEnabled(newState);
      },
    });

    // For profiles sourceType, create export dropdown with both options
    if (insight.sourceType === 'profiles') {
      actions.push({
        text: 'Export',
        intent: 'primary',
        allowed: !reportDisabled && canExport,
        onClick: () => {}, // Required by TypeScript but not used when actions array is present
        actions: [
          {
            text: 'Export PDF',
            icon: 'file-pdf',
            intent: 'danger',
            allowed: !reportDisabled && canExport,
            onClick: () => {
              analytics.events.insights.exportViewClicked({
                insightId: insight.id,
                exportType: 'profiles-pdf',
              });
              return exportReport(
                insight.id,
                'profiles-pdf',
                getReportOptions(currentFilter, columnApi!)
              );
            },
          },
          {
            text: 'Export Excel',
            icon: 'file-excel',
            intent: 'success',
            allowed: !reportDisabled && canExport,
            onClick: () => {
              analytics.events.insights.exportViewClicked({
                insightId: insight.id,
                exportType: 'profiles-excel',
              });
              return exportReport(
                insight.id,
                'profiles-excel',
                getReportOptions(currentFilter, columnApi!)
              );
            },
          },
        ],
      });
    } else {
      // For other sourceTypes, use the exportType as before
      const exportTypes = Array.isArray(insight.exportType)
        ? insight.exportType
        : insight.exportType !== null
        ? [insight.exportType]
        : [];

      for (const exportType of exportTypes) {
        actions.push({
          // Intent, Icon + Text
          ...getExportTypeProps(exportType, canExport),
          allowed: !reportDisabled,
          onClick: () => {
            analytics.events.insights.exportViewClicked({
              insightId: insight.id,
              exportType,
            });
            return exportReport(
              insight.id,
              exportType,
              getReportOptions(currentFilter, columnApi!)
            );
          },
        });
      }
    }

    return actions;
  }, [
    analytics.events.insights,
    autoSaveEnabled,
    canEdit,
    canExport,
    columnApi,
    currentFilter,
    editReportForm,
    exportReport,
    insight,
    isSaving,
    reportDisabled,
  ]);

  const onFilterHandler = (filters: any) => {
    const ratingFilter = [];
    for (const filterKey of Object.keys(filters)) {
      if (filterKey.startsWith('ratingSystem_')) {
        ratingFilter.push({
          key: filterKey.split('_')[1],
          include: filters[filterKey][0],
        });
      }
    }
    setCurrentFilter({
      ...currentFilter,
      profileTypes: filters.profileTypes ?? [],
      tags: filters.tags ?? [],
      ratings: ratingFilter,
    });
  };

  const conditionalPanelProps = {
    condition: currentFilter?.condition,
    onChange: (condition: any) => {
      setCurrentFilter({
        ...currentFilter,
        condition,
      });
    },
  };

  const reportColorRules: ReportColorRules | undefined =
    currentFilter?.reportColorRules;
  const reportColorsPanelProps = {
    reportColorRules,
    onChange: (color: 'green' | 'amber' | 'red', condition: any) => {
      setCurrentFilter({
        ...currentFilter,
        reportColorRules: {
          ...(currentFilter?.reportColorRules || {}),
          [color]: condition,
        },
      });
    },
  };

  return (
    <>
      <PageHeader noWrapper actions={actions} />

      <BuildingReportComponent {...reportProps} />
      <>
        <div className={styles.filterBar}>
          <ProfilesFilterBar
            initialFilters={insight.options}
            onFilter={onFilterHandler}
          />
        </div>

        {/* @ts-ignore React 18 Context.Provider JSX typing mismatch */}
        <ConditionalPanelOnChangeContext.Provider value={conditionalPanelProps}>
          {/* @ts-ignore React 18 Context.Provider JSX typing mismatch */}
          <ReportColorsPanelOnChangeContext.Provider value={reportColorsPanelProps}>
            {/* @ts-ignore React 18 Context.Provider JSX typing mismatch */}
            <ConditionalScheme.Provider value={organisation.scheme}>
              <GridView
                allowEdit={false}
                canEdit={canEdit}
                dataSource={dataSource}
                // @ts-ignore
                contextMenuItems={contextMenuItems}
                onGridReady={setGridApi}
                groups={groups}
                columns={columns}
                filter={currentFilter}
                frameworkComponents={frameworkComponents}
                {...computedGridOptions}
              />
            </ConditionalScheme.Provider>
          </ReportColorsPanelOnChangeContext.Provider>
        </ConditionalPanelOnChangeContext.Provider>
      </>
    </>
  );
};

export default ProfilesGrid;
